import { useEffect, useRef, useState, useCallback } from 'react';
import StaggerText from '@/components/base/StaggerText';

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false);
  const [bgOffset, setBgOffset] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      const heroEl = sectionRef.current;
      if (heroEl) {
        const rect = heroEl.getBoundingClientRect();
        const heroHeight = heroEl.offsetHeight;
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          const progress = Math.max(0, -rect.top) / heroHeight;
          setBgOffset(progress * 60);
        }
      }
      rafRef.current = 0;
    });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [handleScroll]);

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative w-full min-h-screen flex items-end overflow-hidden"
    >
      {/* Full-bleed background image with parallax + slow zoom */}
      <div className="absolute inset-0 will-change-transform">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20futuristic%20artificial%20intelligence%20visualization%2C%20abstract%20neural%20network%20nodes%20with%20glowing%20connections%20on%20deep%20black%20background%2C%20subtle%20aurora%20green%20and%20amber%20light%20trails%2C%20cinematic%20dramatic%20lighting%2C%20minimal%20clean%20composition%2C%20high-end%20tech%20aesthetic%2C%20no%20text&width=1800&height=1200&seq=hero-ai-dark-industrial-2026&orientation=landscape"
          alt=""
          className="w-full h-[calc(100%+60px)] object-cover object-top animate-hero-bg-zoom"
          style={{ transform: `translateY(${-bgOffset}px)` }}
          width={1800}
          height={1200}
        />
      </div>

      {/* Multi-layer overlay for cinematic depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/80" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent" />

      {/* Animated accent glow spot */}
      <div
        className="absolute top-1/3 right-1/4 w-64 h-64 rounded-full blur-[120px] animate-breathe"
        style={{ background: 'oklch(var(--primary-500) / 0.08)' }}
      />

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pb-20 md:pb-28 pt-32">
        <div className="max-w-5xl">
          {/* Overline */}
          <div
            className={`mb-6 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '0.2s' }}
          >
          </div>

          {/* Main headline */}
          <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold text-white leading-[0.95] tracking-tight">
            <span
              className={`block transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '0.4s' }}
            >
              <StaggerText
                text="AI in front of"
                delay={0.5}
                staggerDelay={0.04}
              />
            </span>
            <span
              className={`block mt-2 md:mt-3 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '0.7s' }}
            >
              <span className="text-primary-400">
                <StaggerText
                  text="innovation"
                  delay={1.0}
                  staggerDelay={0.05}
                />
              </span>
            </span>
          </h1>

          {/* Subtitle */}
          <p
            className={`mt-8 md:mt-10 text-base md:text-lg text-white/60 max-w-xl leading-relaxed transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: '1.2s' }}
          >
            AInnovation put artificial intelligence at the forefront of every build －custom software, applied AI and the marketing tools that run on it.
            One team for the whole stack.
          </p>
        </div>
      </div>
    </section>
  );
}